/**
 * [Item 2] AutoDraftContext - mount-stable home for the snake auto-draft
 * engine.
 *
 * The toggle UI lives in the right-rail Queue panel, but the auto-submit
 * effect MUST NOT: `TabbedPanel` renders only the active tab, so anything
 * mounted inside the Queue tab dies the moment the manager looks at Pick
 * History - and auto-draft would silently stop. So the hook is instantiated
 * exactly ONCE here, around the whole snake shell, and the toggle is a thin
 * consumer. Two instances would race their auto-submits and desync the
 * switch, hence the single provider.
 *
 * Snake-only: `available` is false outside this provider, which is how the
 * shared DraftQueue component keeps the toggle out of the auction rail.
 */
import { createContext, useContext, useMemo } from "react";
import { useSnakeAutoDraftQueue } from "../hooks/useSnakeAutoDraftQueue";
import { useSnakeDraft } from "./SnakeDraftContext";

const DEFAULT = {
  available: false,
  enabled: false,
  setEnabled: () => {},
  hasQueue: false,
  isMyTurn: false,
  draftNow: async () => {},
};

const AutoDraftContext = createContext(DEFAULT);

export function AutoDraftProvider({ leagueId, userId, children }) {
  const { enabled, setEnabled, hasQueue } = useSnakeAutoDraftQueue(leagueId, userId);
  const { draftState, submitPick } = useSnakeDraft();

  const isMyTurn =
    !!userId &&
    draftState?.state === "DRAFTING" &&
    draftState?.current_picker_id === userId;

  const value = useMemo(
    () => ({
      available: true,
      enabled,
      setEnabled,
      hasQueue,
      isMyTurn,
      draftNow: submitPick,
    }),
    [enabled, setEnabled, hasQueue, isMyTurn, submitPick],
  );

  return (
    <AutoDraftContext.Provider value={value}>
      {children}
    </AutoDraftContext.Provider>
  );
}

export const useAutoDraft = () => useContext(AutoDraftContext);
